import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Dropdown, Flex, Menu, Spin, Tooltip } from 'antd';
import collect from 'collect.js';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CopyOutlined, ExportOutlined, MoreOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import useHistory from '../../hooks/navigation/useHistory';
import useNavigation from '../../hooks/navigation/useNavigation';
import { ScreenEnum, ScreenGroupEnum } from '../../hooks/navigation/Screens';
import { ENVIRONMENTS, FeatureFlags } from '../../services/exports/Constants';
import useAppState from '../../hooks/context/useAppState';
import ServiceProviderSelect from '../service-provider/ServiceProviderSelect';
import ReduxHooks from '../../store/ReduxHooks';
import { actionCreators } from '../../store/actions';
import usePermissions, { ManagerPermission, ManagerRole } from '../../hooks/auth/usePermissions';
import CompanySelect from '../form/business/CompanySelect';
import useScreenType from '../../hooks/context/useScreenType';
import useApiClient from '../../hooks/api/useApiClient';
import useBrand from '../../hooks/context/useBrand';
import FranchiseSelect from '../form/business/FranchiseSelect';
import { Stringable } from '../../services/helpers/Stringable';
import TestRestaurantTag from '../provider-billing/TestRestaurantTag';

// These open foodamigos.io marketing pages — never surfaced on a partner brand.
const FOOD_AMIGOS_ONLY_GROUPS = [ScreenGroupEnum.ROI, ScreenGroupEnum.Pricing];

function Sidebar() {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Components:Layout:Sidebar' });

  const { MagicLinksManager, CompaniesManager } = useApiClient();

  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  const { company } = useSelector((state) => state.currentCompany);
  const { franchise } = useSelector((state) => state.currentFranchise);
  const { store } = useSelector((state) => state.storefront);

  const { setShowSideBar } = useAppState();
  const navItems = useNavigation();

  const history = useHistory();
  const location = useLocation();
  const { isDesktop } = useScreenType();
  const { can, isSuperAdmin, isMerchant, hasAnyRole, hasGlobalAccess, companyACL, franchiseACL } = usePermissions();
  const brand = useBrand();

  // Which rail section owns a URL. An exact item match wins over the URL prefix,
  // because a screen's route no longer always sits under the section it is filed
  // in — Gift Cards keeps its `/online-shop` route (store roles are not granted
  // the Marketing group) while living in Marketing. The prefix match stays as the
  // fallback, and is what keeps detail routes such as
  // `/marketing/promo-codes/7/details` on their section.
  const ownsPath = (item, pathname) =>
    item.key === pathname || collect(item.children ?? []).contains((child) => ownsPath(child, pathname));

  const findGroupForPath = (pathname) => {
    const slug = collect(pathname?.split('/')).get(1);

    return (
      collect(navItems).first((navItem) => ownsPath(navItem, pathname)) ??
      collect(navItems).first((navItem) => navItem.key?.includes(slug))
    );
  };

  const findInitialGroup = () => findGroupForPath(location?.pathname);

  const [groupKey, setGroupKey] = useState(findInitialGroup()?.key);
  const [loadingGroup, setLoadingGroup] = useState(null);

  useEffect(() => {
    setGroupKey(findInitialGroup()?.key);
  }, [location]);

  const subMenuItem = useMemo(() => collect(navItems).firstWhere('key', groupKey), [navItems, groupKey, t]);

  const onGroupMenuItemClick = (key) => {
    const customHandler =
      {
        [ScreenGroupEnum.OrdersPortal]: async () => {
          setLoadingGroup(ScreenGroupEnum.OrdersPortal);
          const { success, data: responseData } = await MagicLinksManager.create({
            app: 'staff',
          });
          setLoadingGroup(null);

          if (!success) {
            return toast.error(t('toasts.failed_to_launch_orders_portal_app'));
          }

          return window.open(responseData.url, '_blank');
        },
        [ScreenGroupEnum.PhoneAnswering]: () => openKey(ScreenEnum.PhoneAnswering),
        [ScreenGroupEnum.Developers]: () => openKey(ScreenEnum.Developers),
        [ScreenGroupEnum.DataDeletionRequests]: () => openKey(ScreenEnum.DataDeletionRequests),
        [ScreenGroupEnum.ReportTechIssue]: () =>
          openKey(company?.id ? ScreenEnum.ReportTechIssue : ScreenEnum.ReportTechIssueOverview),
        [ScreenGroupEnum.ROI]: () => window.open(`https://foodamigos.io/roirechner?lng=${i18n.language}`, '_blank'),
        [ScreenGroupEnum.Pricing]: () => window.open('https://foodamigos.io/preis', '_blank'),
      }[key] ?? null;

    return customHandler ? customHandler() : setGroupKey(key);
  };

  const [openedItem, setOpenedItem] = useState(location?.pathname);

  function openKey(key) {
    history.push(key);

    if (!isDesktop) {
      setShowSideBar(false);
    }
  }

  const group = collect(navItems).firstWhere('key', groupKey);

  function getReservationUrl() {
    const isGerman = company?.country === 'DE';

    if (import.meta.env.VITE_APP_MODE === ENVIRONMENTS.PROD) {
      return isGerman
        ? `https://${company.franchise?.slug}.tischreservieren.com`
        : `https://${company.franchise?.slug}.table-reserve.com`;
    }

    return `https://${company.franchise?.slug}.foodamigos-reservation-testseite.com`;
  }

  function renderProductButton() {
    // Tech issues is FoodAmigos-internal tooling: there is no product to preview.
    if (groupKey === ScreenGroupEnum.ReportTechIssue) {
      return null;
    }

    function getLink() {
      switch (groupKey) {
        case ScreenGroupEnum.Reservations:
          return getReservationUrl();
        case ScreenGroupEnum.Website:
          return store?.domain ? `https://${store?.domain}` : null;
        default:
          return company?.web_shop_url;
      }
    }

    // Labelled rather than icon-only (Artur's round-2 feedback §9): a bare eye
    // read as an information tooltip, so nobody discovered that this opens the
    // live ordering page — a feature partners demoing to prospects want
    // constantly. The arrow signals the new tab; the tooltip names the target.
    return (
      group && (
        // The span matters: antd suppresses mouse events on a disabled button,
        // so a tooltip attached directly to it never fires — precisely when the
        // user most needs to know why it is greyed out.
        <Tooltip title={t('preview.tooltip')}>
          <span className="ml-md inline-block">
            <Button
              size="small"
              href={getLink()}
              target="_blank"
              rel="noreferrer"
              disabled={!getLink()}
              data-testid="sidebar-preview"
            >
              {t('preview.label')} <ExportOutlined />
            </Button>
          </span>
        </Tooltip>
      )
    );
  }

  function setFranchise(value) {
    if (!value) {
      return ReduxHooks.batchActions([actionCreators.currentFranchise.reset(), actionCreators.currentCompany.reset()]);
    }

    if (value.id === franchise?.id) {
      return;
    }

    ReduxHooks.dispatch(actionCreators.currentFranchise.set(value));

    setCompany(value.companies.length === 1 ? collect(value.companies).first() : null);
  }

  async function setCompany(value) {
    if (!value) {
      return ReduxHooks.batchActions(
        collect([actionCreators.currentCompany.reset()])
          .when(!isMerchant, (collection) => collection.push(actionCreators.currentFranchise.reset()))
          .toArray(),
      );
    }

    if (value.id === company?.id) {
      return;
    }

    const { success } = await CompaniesManager.show(value.id);

    if (!success) {
      toast.error(t('toasts.something_went_wrong'));
    }
  }

  const hasMerchantBilling = !!serviceProvider?.enabled_feature_flags?.includes(FeatureFlags.MerchantBilling);

  const quickActionOptions = useMemo(() => {
    if (isMerchant) {
      return [];
    }

    return collect()
      .when(isSuperAdmin, (collection) =>
        collection.push({
          key: 'copy_id',
          label: 'Copy id',
          onClick: () => navigator.clipboard.writeText(company.id),
          icon: <CopyOutlined />,
        }),
      )
      .when(isSuperAdmin && !!company.stripe_id, (collection) =>
        collection.push({
          key: 'copy_stripe_id',
          label: 'Copy stripe id',
          onClick: () => navigator.clipboard.writeText(company.stripe_id),
          icon: <CopyOutlined />,
        }),
      )
      .push({
        key: 'copy_name',
        label: 'Copy name',
        onClick: () => navigator.clipboard.writeText(company.name),
        icon: <CopyOutlined />,
      })
      .when(can(ManagerPermission.AccessPaymentGatewayDashboards) && !!company.stripe_id, (collection) =>
        collection.push({
          key: 'open_stripe_connect_account',
          label: 'Open stripe account',
          onClick: () =>
            window.open(
              new Stringable('https://dashboard.stripe.com')
                .when(import.meta.env.DEV, (value) => value.append('/test'))
                .append(`/connect/accounts/${company.stripe_id}`)
                .toString(),
              '_blank',
            ),
          icon: <ExportOutlined />,
        }),
      )
      .when(
        hasMerchantBilling &&
          can(ManagerPermission.AccessPaymentGatewayDashboards) &&
          company.merchant?.stripe_customers?.length > 0,
        (collection) =>
          collection.push({
            key: 'open_stripe_customer_profile',
            label: 'Open stripe customer profile',
            onClick: () =>
              window.open(
                new Stringable('https://dashboard.stripe.com')
                  .when(import.meta.env.DEV, (value) => value.append('/test'))
                  .append(`/customers/${company.merchant?.stripe_customers[0].id}`)
                  .toString(),
                '_blank',
              ),
            icon: <ExportOutlined />,
          }),
      )
      .when(!!company.adyen_account_holder_url, (collection) =>
        collection.push({
          key: 'open_adyen_account',
          label: 'Open Adyen account',
          onClick: () => window.open(company.adyen_account_holder_url, '_blank'),
          icon: <ExportOutlined />,
        }),
      )
      .toArray();
  }, [isMerchant, company, can, hasMerchantBilling]);

  const renderBrandLogo = () => {
    if (!brand?.isWhiteLabel) {
      return null;
    }

    return (
      <div className="p-xs flex items-center gap-2 border-b-[1px] border-t-none border-r-none border-l-none border-brand-inkGrey-grey_2 border-solid">
        {brand.logoUrl ? (
          <img src={brand.logoUrl} alt={brand.name} className="h-8 object-contain" />
        ) : (
          <span className="text-sm font-semibold truncate">{brand.name}</span>
        )}
      </div>
    );
  };

  const renderBrand = () => {
    if (
      companyACL.includes('*') ||
      hasAnyRole([ManagerRole.Reseller, ManagerRole.SalesManager, ManagerRole.SalesLeadership]) ||
      companyACL.length > 1
    ) {
      return (
        <div className="p-xs border-b-[1px] border-t-none border-r-none border-l-none border-brand-inkGrey-grey_2 border-solid">
          {hasGlobalAccess && (
            <ServiceProviderSelect
              value={serviceProvider}
              onChange={(value) => ReduxHooks.dispatch(actionCreators.currentServiceProvider.set(value))}
              className="mb-xs"
            />
          )}
          {isMerchant && franchiseACL.length > 1 && (
            <FranchiseSelect value={franchise?.id ? franchise : null} onChange={setFranchise} className="mb-xs" />
          )}
          {(!isMerchant || franchise?.companies?.length > 1) && (
            <Flex gap="8px">
              {!isMerchant && isDesktop && company?.id && (
                <Dropdown
                  menu={{
                    items: quickActionOptions,
                  }}
                >
                  <MoreOutlined className="text-black" />
                </Dropdown>
              )}
              <CompanySelect
                value={company?.id ? company : null}
                onChange={setCompany}
                loadOptionsOnMenuOpen
                key={`service-provider-${serviceProvider?.id}-company-select`}
                className="w-full"
              />
            </Flex>
          )}
        </div>
      );
    }

    return null;
  };

  const onOpenChange = (openKeys) => setOpenedItem((current) => collect(openKeys).first((item) => item !== current));

  const renderGroupIcon = (item) => {
    const content = loadingGroup === item.key ? <Spin /> : item.icon;

    if (!item.badge?.show) {
      return content;
    }

    return (
      <Badge dot color={item.badge.color} className="!leading-[20px]">
        {content}
      </Badge>
    );
  };

  // Split in one pass: the product sections, and the entries pinned to the bottom
  // of the rail. The mapper stays inside so it is not an extra memo dependency.
  const [groupMenuItems, bottomGroupMenuItems] = useMemo(() => {
    const toMenuItem = (item) => ({
      key: item.key,
      icon: renderGroupIcon(item),
      // Shown as the hover tooltip while the rail is collapsed.
      label: item.group,
    });

    const visible = navItems.filter((item) => !(brand?.isWhiteLabel && FOOD_AMIGOS_ONLY_GROUPS.includes(item.key)));

    return [
      visible.filter((item) => item.placement !== 'bottom').map(toMenuItem),
      visible.filter((item) => item.placement === 'bottom').map(toMenuItem),
    ];
  }, [navItems, loadingGroup, brand?.isWhiteLabel]);

  return (
    <div id="sidebar">
      <div className="flex h-full w-full">
        <div className="flex h-screen shrink-0 flex-col" style={{ width: 50 }}>
          <Menu
            onClick={(e) => onGroupMenuItemClick(e.key)}
            mode="inline"
            inlineCollapsed
            className="menu-label-small flex-1 overflow-y-auto scrollbar-hide"
            selectedKeys={groupKey ? [groupKey] : []}
            items={groupMenuItems}
            style={{ width: 50 }}
          />
          {bottomGroupMenuItems.length > 0 && (
            <Menu
              onClick={(e) => onGroupMenuItemClick(e.key)}
              mode="inline"
              inlineCollapsed
              className="menu-label-small mt-auto shrink-0 border-t border-b-none border-r-none border-l-none border-solid border-brand-inkGrey-grey_2"
              selectedKeys={
                {
                  [ScreenEnum.Developers]: [ScreenGroupEnum.Developers],
                  [ScreenEnum.ReportTechIssue]: [ScreenGroupEnum.ReportTechIssue],
                  [ScreenEnum.ReportTechIssueOverview]: [ScreenGroupEnum.ReportTechIssue],
                }[location?.pathname] ?? []
              }
              items={bottomGroupMenuItems}
              style={{ width: 50 }}
            />
          )}
        </div>
        <div className="bg-white h-screen w-full flex flex-col">
          {renderBrandLogo()}
          {renderBrand()}
          <div className="p-md flex items-center border-b">
            <h6 className="text-brand-text-default">{subMenuItem?.group}</h6>
            <TestRestaurantTag company={company} className="ml-xs" />
            {renderProductButton()}
          </div>
          <div className="overflow-y-scroll scrollbar-hide border-none">
            <Menu
              mode="inline"
              items={subMenuItem?.children ?? []}
              onClick={(e) => openKey(e.key)}
              defaultSelectedKeys={[openedItem]}
              openKeys={[openedItem]}
              onOpenChange={onOpenChange}
              className="w-full shrink pb-[150px]"
              style={{ border: 0 }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
